import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import AdminFilterBar from './components/AdminFilterBar';
import AdminDataTable from './components/AdminDataTable';
import MainButton from '../../../shared/layout/buttons/MainButton';
import FreshnessBadge from './components/FreshnessBadge';
import AdminIntegrationsSubnav from './components/AdminIntegrationsSubnav';
import { fetchAdminIntegrations } from '../../../services/Http/AdminApi';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';

const StatusPill = ({ value }) => <Pill $tone={statusTone(value)}>{value || 'unknown'}</Pill>;

const AdminIntegrationsList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchStatus, setFetchStatus] = useState('running');
  const loadRequest = useRef(0);
  const [search, setSearch] = useState('');
  const [integrationType, setIntegrationType] = useState('');
  const [isActive, setIsActive] = useState('');

  const load = useCallback(async () => {
    const request = ++loadRequest.current;
    setFetchStatus('running');
    setLoading(true);
    try {
      const resp = await fetchAdminIntegrations({ search, integration_type: integrationType, is_active: isActive, per_page: 50 });
      if (request !== loadRequest.current) return;
      setRows(resp?.data || []);
      setFetchStatus('success');
    } catch (error) {
      if (request !== loadRequest.current) return;
      setFetchStatus('failed');
      toast.error(error?.response?.data?.message || 'Failed to load integrators');
    } finally {
      if (request === loadRequest.current) setLoading(false);
    }
  }, [search, integrationType, isActive]);

  useEffect(() => { load(); return () => { loadRequest.current += 1; }; }, [load]);

  const columns = useMemo(() => ([
    { key: 'name', label: 'Integrator', render: (r) => <LinkCell to={`/integrations/${r.id}`}>{r.name || '—'}</LinkCell> },
    { key: 'organization_name', label: 'Organization' },
    { key: 'venue_name', label: 'Venue' },
    { key: 'integration_type', label: 'Type' },
    { key: 'status', label: 'Status', render: (r) => <StatusPill value={r.status} /> },
    { key: 'webhook_health', label: 'Webhook', render: (r) => <StatusPill value={r.webhook_health} /> },
    { key: 'email', label: 'Email' },
    { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
    { key: 'updated_at', label: 'Updated', render: (r) => (r.updated_at ? new Date(r.updated_at).toLocaleString() : '—') },
  ]), []);

  return (
    <PageContainer>
      <PageHeader><PageTitle>Integrators</PageTitle></PageHeader>
      <PageContent>
        <AdminIntegrationsSubnav />
        <AdminFilterBar>
          <FilterRow>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search integrator/org/type" />
            <Select value={integrationType} onChange={(e) => setIntegrationType(e.target.value)}>
              <option value="">All types</option>
              <option value="custom">Custom</option>
              <option value="shopware">Shopware</option>
              <option value="woocommerce">WooCommerce</option>
              <option value="shopify">Shopify</option>
            </Select>
            <Select value={isActive} onChange={(e) => setIsActive(e.target.value)}>
              <option value="">Any status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </Select>
          </FilterRow>
          <FilterRow>
            <FreshnessBadge status={fetchStatus} sourceType="operational" />
            <MainButton size="small" onClick={load}>Refresh</MainButton>
          </FilterRow>
        </AdminFilterBar>
        {fetchStatus === 'failed' ? <p role="alert">Failed to load integrators</p> : <AdminDataTable columns={columns} rows={rows} loading={loading} emptyText="No integrators found" />}
      </PageContent>
    </PageContainer>
  );
};

export default AdminIntegrationsList;

const statusTone = (value) => {
  const v = String(value || '').toLowerCase();
  if (['healthy', 'success', 'ok', 'active'].includes(v)) return 'success';
  if (['failed', 'timeout', 'error'].includes(v)) return 'danger';
  if (['stale', 'missing', 'inactive'].includes(v)) return 'warning';
  return 'neutral';
};

const FilterRow = styled.div`display:flex;gap: 0.625rem;align-items:center;flex-wrap:wrap;`;
const SearchInput = styled.input`
  width: 300px; border: 1px solid ${({ theme }) => theme.colors.border}; border-radius: 10px; padding: 0.5625rem 0.75rem; background: ${({ theme }) => theme.colors.surface};
`;
const Select = styled(SimpleSelect)`
  border: 1px solid ${({ theme }) => theme.colors.border}; border-radius: 10px; padding: 0.5625rem 0.75rem; background: ${({ theme }) => theme.colors.surface};
`;
const LinkCell = styled(Link)`color: var(--color-primary); text-decoration: none; font-weight: 600;`;
const Pill = styled.span`
  display:inline-flex;align-items:center;padding: 0.25rem 0.5rem;border-radius:999px;font-size: 0.75rem;font-weight:700;text-transform:capitalize;
  background:${({theme,$tone}) => ({ success: theme.colors.successBg, warning: theme.colors.warningBg, danger: theme.colors.dangerBg, neutral: theme.colors.surfaceMuted }[$tone])};
  color:${({theme,$tone}) => ({ success: theme.colors.success, warning: theme.colors.warning, danger: theme.colors.danger, neutral: theme.colors.textMuted }[$tone])};
`;
